import React, { useState, useRef, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Camera as LucideCamera, Upload as LucideUpload, Sparkles } from 'lucide-react';
import './BillScanner-module.css';
import { autoCompressImage } from '../utils/imageCompression';
import stockInAPI from '../services/stockInAPIService';
import OCRRealtimeOverlay from './OCRRealtimeOverlay';
import { inputKindOf } from '../billMemory/client';
import { getAccess } from '../billMemory/api';

// bmVendorId: a vendor picked before scanning (a certain vendor, design P3); never an AI-read one.
const BillScanner = forwardRef(({ onItemsExtracted, onError, bmVendorId = null }, ref) => {
  const [isProcessing, setIsProcessing] = useState(false);
  /**
   * The queued bills — ONE list, each entry `{ id, file, preview }`.
   *
   * This used to be two parallel arrays (files + previews). Files appended synchronously
   * while image previews appended from an async FileReader.onload, so a mixed drop
   * (image + PDF + image) landed the PDF's placeholder at index 0 and the images after it
   * — while the file array kept the drop order. Everything downstream keys off position:
   * the tile showed one bill's name over another's thumbnail, and Remove deleted the wrong
   * bill. Position can no longer lie, because there is only one position.
   */
  const [fileEntries, setFileEntries] = useState([]);
  const fileIdRef = useRef(0);
  const pastedFilesRef = useRef(new WeakSet()); // Bill Memory: the files the paste box made

  // Derived views. Every consumer below (processing indices, fileStatuses keys, the
  // outward payload) keeps working unchanged — and they are now aligned by construction
  // rather than by hoping two setState calls interleave in the right order.
  const selectedFiles = useMemo(() => fileEntries.map(entry => entry.file), [fileEntries]);
  const previewImages = useMemo(() => fileEntries.map(entry => entry.preview), [fileEntries]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [extractedItems, setExtractedItems] = useState([]);
  const [rawOcrText, setRawOcrText] = useState('');
  const [showRawText, setShowRawText] = useState(true); // default visible so user instantly sees it
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0); // Track which image is being processed
  const [financialData, setFinancialData] = useState(null); // Store OCR financial data (grand_total, etc.)
  const [selectedImageModal, setSelectedImageModal] = useState(null); // For full-screen image preview
  const [vendorInfo, setVendorInfo] = useState(null); // Store vendor info from OCR

  // Pre-continue confirmation popup (tax detected / document-intent warnings)
  const [showTaxConfirm, setShowTaxConfirm] = useState(false);
  const [pendingExtractedData, setPendingExtractedData] = useState(null);
  const [confirmMessages, setConfirmMessages] = useState([]);

  // === MULTI-IMAGE PRODUCTION STATE ===
  const [sameBillMode, setSameBillMode] = useState(true); // true = pages of same bill, false = separate bills
  // H3 — keyed by the entry's STABLE id, never by its position.
  //
  // The file list was already migrated to ids; the processing bookkeeping was not, and the
  // two disagreed the moment the list shifted. Repro: 3 bills → process → #2 fails → remove
  // #1 (allowed once processing ends) → every later index is off by one, so "Retry 1 Failed
  // File" retried what used to be #3 and wrote its result over the wrong key, and every
  // OK/ERR badge sat on a neighbour's tile. A position is a rendering detail; it must never
  // be a key.
  const [fileStatuses, setFileStatuses] = useState({}); // { [fileId]: 'pending'|'compressing'|'uploading'|'processing'|'done'|'failed' }
  const [fileResults, setFileResults] = useState({});   // { [fileId]: { items, rawText, vendor, financial, error } }
  const [failedFileIds, setFailedFileIds] = useState([]); // ids of files that failed

  /**
   * Whether a completed run's results are currently on screen (S1).
   *
   * Read inside ingestFiles, which is a useCallback the file picker and the drop zone both
   * hold onto — a ref rather than the state itself so adding it does not rebuild those
   * handlers on every OCR result, and so the check can never read a stale value.
   */
  const hasCompletedResultsRef = useRef(false);

  // OCR Real-time Overlay State
  const [showOCROverlay, setShowOCROverlay] = useState(false);
  const [ocrVendorData, setOcrVendorData] = useState(null);
  const [ocrItems, setOcrItems] = useState([]);
  const [ocrStage, setOcrStage] = useState(0);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrStageName, setOcrStageName] = useState('');
  const [currentOCRSessionId, setCurrentOCRSessionId] = useState(null);
  const [isDragging, setIsDragging] = useState(false); // files hovering over the drop zone
  // Bill Memory: a bill pasted as text (WhatsApp lists) — offered only while Bill Memory and its screen
  // are On (Shadow, every business's default, shows nothing new). /access says so (Fix 13): never the whole memory.
  const [bmTextOn, setBmTextOn] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  useEffect(() => {
    let cancelled = false;
    getAccess().then((a) => {
      if (!cancelled) setBmTextOn(!!(a && a.success && a.paste));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const controllerRef = useRef(null);
  const timeoutRef = useRef(null);
  const isCancelledRef = useRef(false); // Track if user cancelled processing

  // Cleanup function
  const cleanup = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Handle OCR cancellation
  const handleCancelOCR = useCallback(async () => {
    if (currentOCRSessionId) {
      try {
        await stockInAPI.abortOCR(currentOCRSessionId);
        setShowOCROverlay(false);
        setCurrentOCRSessionId(null);
        isCancelledRef.current = true;
        setError('OCR processing cancelled');
      } catch (error) {
        console.error('Failed to abort OCR:', error);
      }
    } else {
      setShowOCROverlay(false);
    }
  }, [currentOCRSessionId]);

  // Initialize WebSocket for OCR real-time updates
  useEffect(() => {
    const businessId = localStorage.getItem('businessId');
    if (businessId) {
      stockInAPI.initializeWebSocket(businessId);

      // Subscribe to OCR events
      const unsubscribers = [
        stockInAPI.on('ocr-started', (data) => {
          setShowOCROverlay(true);
          setOcrVendorData(null);
          setOcrItems([]);
          setOcrStage(1);
          setOcrProgress(0);
          setOcrStageName('OCR Extraction');
          setCurrentOCRSessionId(data.sessionId);
        }),

        stockInAPI.on('ocr-stage-complete', (data) => {
          setOcrStage(data.stage);
          setOcrProgress(data.progress);
          setOcrStageName(data.stageName);
        }),

        stockInAPI.on('ocr-vendor-extracted', (data) => {
          setOcrVendorData(data);
          setOcrProgress(data.progress);
        }),

        stockInAPI.on('ocr-item-extracted', (data) => {
          setOcrItems(prev => [...prev, data.item]);
          setOcrProgress(data.progress);
        }),

        stockInAPI.on('ocr-complete', (data) => {
          setOcrProgress(100);
          // Keep overlay open briefly to show completion, then auto-close
          setTimeout(() => {
            setShowOCROverlay(false);
            setCurrentOCRSessionId(null);
          }, 2000);
        }),

        stockInAPI.on('ocr-error', (data) => {
          console.error('OCR Error Event:', data);
          setShowOCROverlay(false);
          setError(`OCR Error: ${data.error}`);
          setCurrentOCRSessionId(null);
        }),

        stockInAPI.on('ocr-aborted', (data) => {
          console.warn('OCR Aborted Event:', data);
          setShowOCROverlay(false);
          setError('OCR processing was cancelled');
          setCurrentOCRSessionId(null);
        })
      ];

      return () => {
        unsubscribers.forEach(unsub => unsub());
      };
    }
  }, []);

  // Handle file selection - UPDATED FOR MULTIPLE FILES
  // Excel/CSV spreadsheet exports are accepted as a stock-in source (parsed on the backend, no OCR)
  const isExcelFile = useCallback((file) => {
    const name = (file?.name || '').toLowerCase();
    const type = file?.type || '';
    return /\.(xlsx|xls|xlsm|csv|ods)$/.test(name) ||
      type.includes('spreadsheet') || type.includes('excel') || type === 'text/csv';
  }, []);

  /**
   * Take a batch of files into the scanner: validate, preview, queue.
   *
   * Shared by the file picker and by drag-and-drop so a dropped bill goes through exactly
   * the same type and size checks as a picked one. Splitting these into two paths is how a
   * 60MB PDF ends up queued from one entry point and rejected from the other.
   */
  const ingestFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    // S1 — adding a bill after a completed run re-scans EVERYTHING and throws away the
    // results already on screen. That used to happen silently, mid-review. Ask first.
    if (hasCompletedResultsRef.current) {
      const proceed = window.confirm(
        'Adding another bill will re-scan everything and clear the current results. Continue?'
      );
      if (!proceed) return;
    }

    // Cleanup previous state
    setError(null);
    setSuccess(null);
    setExtractedItems([]);
    setRawOcrText('');
    setProcessingProgress(0);
    setVendorInfo(null);
    setFinancialData(null);
    // H3 — the per-file processing state belongs to the run that produced it. A new run
    // reprocesses every file, so carrying old statuses/results/failures into it leaves OK
    // and ERR badges from a previous scan sitting on tiles this run has not touched yet.
    setFileStatuses({});
    setFileResults({});
    setFailedFileIds([]);

    // previews are data: URLs; if switching to object URLs, revoke per-entry on removal
    // only — never revoke the whole list on append.

    // Validate each file — bill photos, PDFs AND Excel/CSV sheets are all valid
    // stock-in sources (photos/PDFs go to the AI parser, sheets are parsed directly)
    const validFiles = [];

    for (const file of files) {
      const isImage = (file.type || '').startsWith('image/');
      const isPDF = file.type === 'application/pdf';
      const isExcel = isExcelFile(file);

      if (!isImage && !isPDF && !isExcel) {
        setError(`File ${file.name} is not supported. Upload a bill photo (JPG/PNG), a PDF, or an Excel sheet (.xlsx, .xls, .csv, .ods).`);
        continue;
      }

      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setError(`File ${file.name} is too large. Please select files smaller than 50MB.`);
        continue;
      }

      // The id is minted here and never changes. Previews arrive later and out of order;
      // they find their own row by id, so a slow-loading thumbnail can never overwrite a
      // neighbour's slot and a file removed mid-read simply has nothing to patch.
      fileIdRef.current += 1;
      const id = `bill-${fileIdRef.current}`;
      validFiles.push({ id, file, preview: 'PDF_PLACEHOLDER' });

      // Previews: images render a thumbnail; PDFs/sheets keep the placeholder tile
      if (isImage) {
        const reader = new FileReader();
        reader.onload = (e) => setFileEntries(prev => prev.map(
          entry => (entry.id === id ? { ...entry, preview: e.target.result } : entry)
        ));
        reader.onerror = () => setError(`Failed to read file: ${file.name}`);
        reader.readAsDataURL(file);
      }
    }

    setFileEntries(prev => [...prev, ...validFiles]);
  }, [isExcelFile]);

  // Keeps the S1 confirm honest: it fires only when there is something to lose.
  useEffect(() => {
    hasCompletedResultsRef.current = extractedItems.length > 0 || Boolean((rawOcrText || '').trim());
  }, [extractedItems, rawOcrText]);

  // Bill Memory: the pasted text joins the list as one bill (read as text by the same parser).
  const addPastedText = useCallback(() => {
    const text = pasteText.trim();
    if (!text) return;
    fileIdRef.current += 1;
    const id = `bill-${fileIdRef.current}`;
    const file = new File([text], `pasted-bill-${fileIdRef.current}.txt`, { type: 'text/plain' });
    pastedFilesRef.current.add(file);
    setFileEntries(prev => [...prev, { id, file, preview: 'PDF_PLACEHOLDER' }]);
    setPasteText('');
    setShowPaste(false);
  }, [pasteText]);

  const handleFileSelect = useCallback((event) => {
    ingestFiles(event.target.files);
    // Clear the input so picking the SAME file again still fires onChange. Without this a
    // user who removes a bill and re-picks it gets nothing and no explanation.
    event.target.value = '';
  }, [ingestFiles]);

  // ---------------------------------------------------------------------------
  // Drag and drop
  // ---------------------------------------------------------------------------
  /**
   * Nesting depth of the current drag.
   *
   * dragenter and dragleave both fire for every descendant the pointer crosses and both
   * bubble to this container, so moving from the card's edge toward its icon produces a
   * dragleave while the pointer is still very much inside the zone. Clearing the highlight
   * on that makes it strobe as the user aims.
   *
   * Counting the pairs is what fixes it. relatedTarget would be the tidier test, but
   * dragleave does not populate it reliably across browsers — Safari leaves it null — so
   * the count is the portable answer.
   */
  const dragDepthRef = useRef(0);

  /** True only for a drag actually carrying files — not selected text or a link. */
  const dragHasFiles = useCallback((event) => {
    const types = event?.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  }, []);

  const handleDragEnter = useCallback((event) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, [dragHasFiles]);

  const handleDragOver = useCallback((event) => {
    if (!dragHasFiles(event)) return;
    // Both preventDefault AND the copy effect are required: without them the browser
    // refuses the drop and falls back to opening the file in the tab.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [dragHasFiles]);

  const handleDragLeave = useCallback((event) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, [dragHasFiles]);

  const handleDrop = useCallback((event) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    ingestFiles(event.dataTransfer.files);
  }, [dragHasFiles, ingestFiles]);

  // A file dropped slightly wide of the zone would otherwise be opened by the browser,
  // navigating away from the scanner and discarding anything already queued. Swallow
  // stray drops for as long as the scanner is on screen.
  useEffect(() => {
    const swallow = (e) => {
      const types = e.dataTransfer && e.dataTransfer.types;
      if (types && Array.from(types).includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  // Handle camera capture
  const handleCameraCapture = useCallback(() => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Cancel processing
  const cancelProcessing = useCallback(() => {
    isCancelledRef.current = true; // Set cancellation flag
    cleanup();
    setIsProcessing(false);
    setProcessingProgress(0);
    setError('Processing cancelled');
    // Note: Keep images so user can retry - use "Clear All" button to reset completely
  }, [cleanup]);

  // Update progress with safety checks
  const updateProgress = useCallback((progress) => {
    setProcessingProgress(prevProgress => {
      const newProgress = Math.min(Math.max(progress, prevProgress), 100);
      return newProgress;
    });
  }, []);

  // ============================================================================
  // PRODUCTION MULTI-IMAGE PROCESSING ENGINE
  // ============================================================================

  /**
   * Process a single file: compress (if image), upload, return result.
   * Used by the parallel processing engine below.
   */
  const processSingleFile = useCallback(async (file, fileId, fileIndex, onStatusChange) => {
    const isPDF = file.type === 'application/pdf';
    const isExcel = isExcelFile(file);
    // Bill Memory: a bill pasted as text — only what the paste box made; a .csv the browser labels
    // text/plain is still a spreadsheet upload, exactly as today.
    const isPastedText = pastedFilesRef.current.has(file);
    let fileToUpload = file;

    // 1. Compress if image (never compress PDFs, spreadsheets or pasted text — upload as-is)
    onStatusChange(fileId, 'compressing');
    if (!isPDF && !isExcel && !isPastedText) {
      try {
        fileToUpload = await autoCompressImage(file);
      } catch (compressErr) {
        console.warn(`Compression failed for ${file.name}, using original`, compressErr);
        fileToUpload = file;
      }
    }

    // 2. Upload with 90-second timeout
    onStatusChange(fileId, 'uploading');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 90000);

    try {
      const bmOpts = bmVendorId ? { bmVendorId } : {};
      const uploadResult = isPastedText
        ? await stockInAPI.uploadReceipt(null, undefined, { ...bmOpts, billText: await file.text() })
        : await stockInAPI.uploadReceipt(fileToUpload, undefined, bmOpts);
      clearTimeout(timeout);

      if (!uploadResult || !uploadResult.items) {
        throw new Error('OCR returned no items');
      }

      onStatusChange(fileId, 'done');

      const extracted = uploadResult.items || [];
      const rawText = uploadResult.extracted_text || uploadResult.text || '';
      const vendorName = uploadResult.vendor_name || uploadResult.vendor?.name || null;
      const vendorPhone = uploadResult.vendor_phone || uploadResult.vendor?.phone || null;
      const grandTotal = uploadResult.grand_total || uploadResult.financial?.grand_total || null;
      const ocrCgst = uploadResult.cgst || uploadResult.stockInData?.cgst || uploadResult.parsedData?.summary?.cgst || null;
      const ocrSgst = uploadResult.sgst || uploadResult.stockInData?.sgst || uploadResult.parsedData?.summary?.sgst || null;

      // Tag items with source file, and (Bill Memory, Fix 13 row 6) the kind of bill they came from
      const bmInputKind = inputKindOf({ isExcel, isPastedText });
      const taggedItems = extracted.map(item => ({
        ...item,
        sourceFile: file.name,
        sourceFileIndex: fileIndex + 1,
        bm_input_kind: bmInputKind
      }));

      return {
        success: true,
        // The KEY is the id. fileIndex is kept purely as the human-facing ordinal ("File 2")
        // used in the raw-text headers and item tags — it is a label, never a lookup.
        fileId,
        fileIndex,
        fileName: file.name,
        items: taggedItems,
        rawText,
        vendorName,
        vendorPhone,
        vendor: uploadResult.vendor || (vendorName ? { name: vendorName, phone: vendorPhone } : null),
        grandTotal,
        cgst: ocrCgst,
        sgst: ocrSgst,
        taxDetected: uploadResult.tax_detected || false,
        invoiceNumber: uploadResult.invoice_number || null,
        invoiceDate: uploadResult.invoice_date || null,
        // The server's reading of that printed date (utils/billDate): YYYY-MM-DD + verdict
        invoiceDateIso: uploadResult.invoice_date_iso || null,
        invoiceDateStatus: uploadResult.invoice_date_status || null,
        invoiceDateDaysAgo: uploadResult.invoice_date_days_ago ?? null,
        documentWarning: uploadResult.document_warning || null,
        summaryLine: uploadResult.summary_line || null,
        billMemory: uploadResult.bill_memory || null,
        financial: uploadResult.financial || null,
        sessionId: uploadResult.sessionId || null
      };
    } catch (err) {
      clearTimeout(timeout);
      onStatusChange(fileId, 'failed');
      console.error(`File ${fileIndex + 1} (${file.name}) failed:`, err.message);
      return {
        success: false,
        fileId,
        fileIndex,
        fileName: file.name,
        error: err.message || 'Unknown error',
        items: [],
        rawText: ''
      };
    }
  }, [isExcelFile, bmVendorId]);

  /**
   * Cross-file deduplication: detect items that appear in multiple files
   * with similar names and quantities (e.g., same section photographed twice).
   */
  const deduplicateAcrossFiles = useCallback((allItems) => {
    if (allItems.length <= 1) return allItems;

    const normalize = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
    const dedupMap = new Map();
    const result = [];
    let dupsFound = 0;

    for (const item of allItems) {
      const key = normalize(item.item_name || item.name);
      if (!key) { result.push(item); continue; }

      const existing = dedupMap.get(key);
      if (existing && existing.sourceFileIndex !== item.sourceFileIndex) {
        // Same item name from different files
        const existingQty = parseFloat(existing.quantity) || 0;
        const currentQty = parseFloat(item.quantity) || 0;
        // If quantities are within 20% of each other, it's likely a duplicate
        if (existingQty > 0 && currentQty > 0) {
          const ratio = Math.min(existingQty, currentQty) / Math.max(existingQty, currentQty);
          if (ratio >= 0.8) {
            dupsFound++;
            // Keep the one with higher price info (more complete data)
            const existingPrice = parseFloat(existing.unit_price || existing.rate) || 0;
            const currentPrice = parseFloat(item.unit_price || item.rate) || 0;
            if (currentPrice > existingPrice) {
              // Replace with current (better data)
              const idx = result.indexOf(existing);
              if (idx >= 0) result[idx] = { ...item, _dedup_note: `Merged from file ${existing.sourceFileIndex}` };
            }
            continue; // Skip duplicate
          }
        }
      }

      dedupMap.set(key, item);
      result.push(item);
    }

    return result;
  }, []);

  /**
   * Smart vendor/financial merging from multiple file results.
   * - Vendor: picks the most complete vendor record (has both name and phone)
   * - Grand total: in same-bill mode, picks the largest non-null total; in different-bills mode, sums all totals
   */
  const mergeVendorAndFinancials = useCallback((results, isSameBill) => {
    let bestVendor = null;
    let bestVendorScore = 0;
    let grandTotals = [];
    let bestFinancial = null;
    let cgstValues = [];
    let sgstValues = [];

    for (const r of results) {
      if (!r.success) continue;

      // Score vendor completeness: name=1, phone=1, both=3 (bonus for completeness)
      let score = 0;
      if (r.vendorName) score += 1;
      if (r.vendorPhone) score += 1;
      if (r.vendorName && r.vendorPhone) score += 1;
      if (score > bestVendorScore) {
        bestVendorScore = score;
        bestVendor = {
          name: r.vendorName,
          phone: r.vendorPhone,
          vendor: r.vendor
        };
      }

      if (r.grandTotal) grandTotals.push(parseFloat(r.grandTotal));
      if (r.financial && !bestFinancial) bestFinancial = r.financial;

      // Track CGST/SGST — pick first non-null values (same bill) or sum (different bills)
      if (r.cgst) cgstValues.push(parseFloat(r.cgst));
      if (r.sgst) sgstValues.push(parseFloat(r.sgst));
    }

    let mergedGrandTotal = null;
    if (grandTotals.length > 0) {
      if (isSameBill) {
        mergedGrandTotal = Math.max(...grandTotals);
      } else {
        mergedGrandTotal = grandTotals.reduce((a, b) => a + b, 0);
      }
    }

    // Merge CGST/SGST: same bill → max (avoid double-counting), different bills → sum
    let mergedCgst = null;
    let mergedSgst = null;
    if (cgstValues.length > 0) {
      mergedCgst = isSameBill ? Math.max(...cgstValues) : cgstValues.reduce((a, b) => a + b, 0);
    }
    if (sgstValues.length > 0) {
      mergedSgst = isSameBill ? Math.max(...sgstValues) : sgstValues.reduce((a, b) => a + b, 0);
    }

    return { bestVendor, mergedGrandTotal, bestFinancial, mergedCgst, mergedSgst };
  }, []);

  // Process all images — PARALLEL with concurrency limit
  const processImage = useCallback(async (retryIds = null) => {
    // Built from the entries themselves, so a file is always paired with its OWN id, and a
    // retry targets the file the user saw fail — not whatever has since slid into its slot.
    const allEntries = fileEntries.map((entry, i) => ({ id: entry.id, file: entry.file, index: i }));
    const filesToProcess = retryIds
      ? allEntries.filter(e => retryIds.includes(e.id))
      : allEntries;

    if (filesToProcess.length === 0) {
      setError('Please select at least one image first');
      return;
    }

    isCancelledRef.current = false;

    // Reset states (keep previous results if retrying)
    setIsProcessing(true);
    setError(null);
    setSuccess(null);
    setProcessingProgress(5); // Start at 5% so user sees immediate movement
    setCurrentProcessingIndex(0);
    if (!retryIds) {
      setExtractedItems([]);
      setRawOcrText('');
      setVendorInfo(null);
      setFinancialData(null);
      setFileResults({});
      setFailedFileIds([]);
      // Initialize all file statuses
      const initialStatuses = {};
      allEntries.forEach(({ id }) => { initialStatuses[id] = 'pending'; });
      setFileStatuses(initialStatuses);
    } else {
      // For retry: reset only failed file statuses
      setFileStatuses(prev => {
        const updated = { ...prev };
        retryIds.forEach(id => { updated[id] = 'pending'; });
        return updated;
      });
      setFailedFileIds([]);
    }

    const statusUpdater = (fileId, status) => {
      setFileStatuses(prev => {
        const updated = { ...prev, [fileId]: status };
        // Calculate progress: in-progress statuses get partial credit
        const allStatuses = Object.values(updated);
        const totalCount = allEntries.length;
        let progressSum = 0;
        for (const s of allStatuses) {
          if (s === 'done') progressSum += 1;
          else if (s === 'failed') progressSum += 1;
          else if (s === 'processing') progressSum += 0.7;
          else if (s === 'uploading') progressSum += 0.4;
          else if (s === 'compressing') progressSum += 0.15;
          // 'pending' = 0
        }
        const progressPercent = Math.round((progressSum / totalCount) * 100);
        // Never go below 5% once processing has started
        setProcessingProgress(Math.max(5, Math.min(progressPercent, 99)));
        const doneCount = allStatuses.filter(s => s === 'done' || s === 'failed').length;
        setCurrentProcessingIndex(doneCount);
        return updated;
      });
    };

    try {
      // Parallel processing with concurrency limit of 3
      const CONCURRENCY = 3;
      const allResults = [];

      for (let batch = 0; batch < filesToProcess.length; batch += CONCURRENCY) {
        if (isCancelledRef.current) {
          setIsProcessing(false);
          return;
        }

        const batchFiles = filesToProcess.slice(batch, batch + CONCURRENCY);

        const batchPromises = batchFiles.map(({ id, file, index }) =>
          processSingleFile(file, id, index, statusUpdater)
        );

        // Use Promise.allSettled so one failure doesn't kill the batch
        const settled = await Promise.allSettled(batchPromises);
        for (const result of settled) {
          if (result.status === 'fulfilled') {
            allResults.push(result.value);
            // Store per-file result
            setFileResults(prev => ({ ...prev, [result.value.fileId]: result.value }));
          } else {
            // Promise itself rejected (shouldn't happen since processSingleFile catches internally)
            console.error('Unexpected promise rejection:', result.reason);
          }
        }
      }

      if (isCancelledRef.current) return;

      // Separate successes and failures
      const successes = allResults.filter(r => r.success);
      const failures = allResults.filter(r => !r.success);
      setFailedFileIds(failures.map(r => r.fileId));

      if (successes.length === 0) {
        setError(`All ${failures.length} file(s) failed to process. Please try again with clearer images.`);
        setIsProcessing(false);
        return;
      }

      // Combine all successful items. Only build a raw-text block for files that actually
      // produced OCR text — Excel/CSV uploads have no OCR text, so we skip them (otherwise
      // the "Raw OCR Text" panel would show just a "--- File N: name ---" header).
      let allItems = successes.flatMap(r => r.items);
      const allRawTexts = successes
        .filter(r => (r.rawText || '').trim().length > 0)
        .map(r => `\n--- File ${r.fileIndex + 1}: ${r.fileName} ---\n${r.rawText}`);

      // Merge with any previously successful results (from retry scenario)
      if (retryIds) {
        const prevResults = Object.values(fileResults).filter(r => r && r.success && !retryIds.includes(r.fileId));
        const prevItems = prevResults.flatMap(r => r.items || []);
        const prevTexts = prevResults
          .filter(r => (r.rawText || '').trim().length > 0)
          .map(r => `\n--- File ${r.fileIndex + 1}: ${r.fileName} ---\n${r.rawText}`);
        allItems = [...prevItems, ...allItems];
        allRawTexts.unshift(...prevTexts);
      }

      // Cross-file deduplication (only in same-bill mode — separate bills may legitimately have same items)
      if (sameBillMode && successes.length > 1) {
        allItems = deduplicateAcrossFiles(allItems);
      }

      // Smart vendor/financial merging
      const { bestVendor, mergedGrandTotal, bestFinancial, mergedCgst, mergedSgst } = mergeVendorAndFinancials(allResults, sameBillMode);

      const combinedRawText = allRawTexts.join('\n');

      // Capture session ID from last successful result
      const lastSessionId = successes[successes.length - 1]?.sessionId;
      if (lastSessionId) setCurrentOCRSessionId(lastSessionId);

      setExtractedItems(allItems);
      setRawOcrText(combinedRawText);
      updateProgress(100);
      setProcessingProgress(100);

      const tempVendorInfo = bestVendor ? {
        ...bestVendor,
        grandTotal: mergedGrandTotal,
        financial: bestFinancial
      } : null;
      setVendorInfo(tempVendorInfo);
      if (mergedGrandTotal) setFinancialData({ grand_total: mergedGrandTotal, financial: bestFinancial });

      // Build success/warning message
      let statusMsg = `Successfully extracted ${allItems.length} items from ${successes.length} file(s)`;
      if (failures.length > 0) {
        statusMsg += ` (${failures.length} file(s) failed — you can retry them)`;
      }

      // Pre-continue confirmations: tax detected (Excel) and/or document warnings
      // (quotation/estimate instead of invoice, unreadable photo, multiple bills)
      const taxDetected = successes.some(r => r && r.taxDetected);
      const documentWarnings = [...new Set(successes.map(r => r && r.documentWarning).filter(Boolean))];
      const firstInvoice = successes.find(r => r && r.invoiceNumber);
      // The bill date comes from the first file that HAS one — a page with the invoice
      // number may not carry the date. Every distinct date the server could read is kept,
      // so the form can say when several bills disagree (they still file as one stock-in).
      const firstDated = successes.find(r => r && r.invoiceDateIso) || successes.find(r => r && r.invoiceDate);
      const billDates = [...new Set(successes.map(r => r && r.invoiceDateIso).filter(Boolean))].sort();

      const extractedPayload = {
        items: allItems,
        rawText: combinedRawText,
        filename: selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length}-images`,
        previewImage: previewImages[0] || null,
        previewImages: previewImages,
        fileCount: selectedFiles.length,
        vendor_name: tempVendorInfo?.name || null,
        vendor_phone: tempVendorInfo?.phone || null,
        vendor: tempVendorInfo?.vendor || null,
        grand_total: mergedGrandTotal || null,
        financial: bestFinancial || null,
        cgst: mergedCgst || null,
        sgst: mergedSgst || null,
        tax_detected: taxDetected, // form auto-enables per-item tax when true
        invoice_number: firstInvoice?.invoiceNumber || null,
        invoice_date: firstDated?.invoiceDate || null,
        invoice_date_iso: firstDated?.invoiceDateIso || null,
        invoice_date_status: firstDated?.invoiceDateStatus || 'missing',
        invoice_date_days_ago: firstDated?.invoiceDateDaysAgo ?? null,
        bill_dates: billDates,
        // Bill Memory: one record per uploaded file (null while Bill Memory is Off)
        bill_memory: (() => {
          const perFile = successes.map(r => r && r.billMemory).filter(Boolean);
          return perFile.length ? { mode: perFile[0].mode, screen: perFile[0].screen, perFile } : null;
        })(),
      };

      // Small delay to show 100% completion
      setTimeout(() => {
        if (isCancelledRef.current) return;

        setSuccess(statusMsg);
        setIsProcessing(false);
        setCurrentProcessingIndex(0);
        setTimeout(() => setProcessingProgress(0), 2000);

        const messages = [];
        if (taxDetected) messages.push('Tax is enabled for this stock-in purchase.');
        for (const w of documentWarnings) messages.push(w);

        if (messages.length > 0) {
          // Needs explicit user confirmation before continuing to the stock-in form
          setConfirmMessages(messages);
          setPendingExtractedData(extractedPayload);
          setShowTaxConfirm(true);
        } else if (onItemsExtracted) {
          onItemsExtracted(extractedPayload);
        }
      }, 500);

    } catch (error) {
      console.error('Multi-image processing error:', error);
      setError((error && typeof error.message === 'string') ? error.message : 'Failed to process images');
      setIsProcessing(false);
      setCurrentProcessingIndex(0);
      setTimeout(() => setProcessingProgress(0), 2000);
      if (onError) onError({ ...error, previewImages });
    } finally {
      cleanup();
    }
  }, [fileEntries, selectedFiles, sameBillMode, fileResults, onItemsExtracted, onError, previewImages, updateProgress, cleanup, processSingleFile, deduplicateAcrossFiles, mergeVendorAndFinancials]);

  // Retry only failed files — by id, so removing a file first cannot re-target the retry.
  const retryFailedFiles = useCallback(() => {
    if (failedFileIds.length > 0) {
      processImage(failedFileIds);
    }
  }, [failedFileIds, processImage]);

  // Reset scanner
  const resetScanner = useCallback(() => {

    // Cleanup any ongoing operations
    cleanup();

    // previews are data: URLs; if switching to object URLs, revoke per-entry on removal
    // only — never revoke the whole list on append.

    // Reset all state
    setFileEntries([]);
    setExtractedItems([]);
    setRawOcrText('');
    setError(null);
    setSuccess(null);
    setProcessingProgress(0);
    setIsProcessing(false);
    setCurrentProcessingIndex(0);
    setVendorInfo(null);
    setFinancialData(null);
    setFileStatuses({});
    setFileResults({});
    setFailedFileIds([]);
    setSameBillMode(true);

    // Clear file inputs
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  }, [cleanup]);

  // Tax-detected popup — OK continues to the stock-in form, Cancel aborts the upload
  const handleTaxConfirmOk = useCallback(() => {
    setShowTaxConfirm(false);
    setConfirmMessages([]);
    const payload = pendingExtractedData;
    setPendingExtractedData(null);
    if (payload && onItemsExtracted) onItemsExtracted(payload);
  }, [pendingExtractedData, onItemsExtracted]);

  const handleTaxConfirmCancel = useCallback(() => {
    setShowTaxConfirm(false);
    setConfirmMessages([]);
    setPendingExtractedData(null);
    resetScanner(); // cancel the operation entirely
  }, [resetScanner]);

  // Expose resetScanner method to parent component via ref
  useImperativeHandle(ref, () => ({
    resetScanner
  }), [resetScanner]);

  // Handle manual entry request
  const handleManualEntry = useCallback(() => {
    if (onError) {
      // Check if processing was cancelled
      if (isCancelledRef.current) {
        return;
      }

      onError(new Error('user_requested_manual_entry'));
    }
  }, [onError]);

  // Remove individual file. Keyed by the entry's id, never by its position in the grid —
  // position is a rendering detail and using it is how the wrong bill got deleted.
  const removeFile = useCallback((id) => {
    // previews are data: URLs; if switching to object URLs, revoke per-entry on removal
    // only — never revoke the whole list on append.
    setFileEntries(prev => prev.filter(entry => entry.id !== id));
    // The processing state is keyed by the same id, so it has to die with the row —
    // otherwise a removed file's OK/ERR result stays in the merge and its id stays in the
    // retry list, pointing at a file that is no longer there.
    setFileStatuses(prev => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFileResults(prev => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFailedFileIds(prev => (prev.includes(id) ? prev.filter(fid => fid !== id) : prev));
  }, []);

  // Handle continue with extracted items
  const handleContinueWithItems = useCallback(() => {
    if (onItemsExtracted && extractedItems.length > 0) {
      const dataToPass = {
        items: extractedItems,
        rawText: rawOcrText,
        filename: selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length}-images`,
        previewImage: previewImages[0] || null,
        previewImages: previewImages,
        fileCount: selectedFiles.length,
        // Include vendor info from state
        vendor_name: vendorInfo?.name || null,
        vendor_phone: vendorInfo?.phone || null,
        vendor: vendorInfo?.vendor || null,
        // Include financial data from OCR
        grand_total: financialData?.grand_total || null,
        financial: financialData?.financial || null
      };
      onItemsExtracted(dataToPass);
    }
  }, [onItemsExtracted, extractedItems, selectedFiles, previewImages, rawOcrText, financialData, vendorInfo]);

  return (
    // The drop target is this wrapper, which is mounted for the scanner's entire life.
    // It used to be the empty-state option stack — which unmounts the moment the first
    // bill lands — so every bill dropped after the first hit the preview grid, which had
    // no handlers, and was then swallowed by the window-level stray-drop guard below.
    // Silent, and indistinguishable from "drag and drop is broken".
    <div
      className="bill-scanner-container"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {(!previewImages || previewImages.length === 0) && (
        // The whole option stack is the drop target, not just the upload card: a bill
        // dropped an inch high — over the Scan Bill card — is still a bill the user meant
        // to upload, and rejecting it there would read as the feature being broken.
        <div
          className={`scanner-options cscanv2-option-stack${isDragging ? ' cscanv2-dropping' : ''}`}
        >
          <div
            className="scan-bill-section cscanv2-scan-card"
            onClick={handleCameraCapture}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleCameraCapture()}
          >
            <span className="cscanv2-corner cscanv2-corner-tl" aria-hidden="true" />
            <span className="cscanv2-corner cscanv2-corner-tr" aria-hidden="true" />
            <span className="cscanv2-corner cscanv2-corner-bl" aria-hidden="true" />
            <span className="cscanv2-corner cscanv2-corner-br" aria-hidden="true" />
            <div className="cscanv2-icon-wrap cscanv2-scan-icon-wrap" aria-hidden="true">
              <LucideCamera className="cscanv2-lucide cscanv2-lucide-scan" strokeWidth={2.25} />
            </div>
            <div className="scan-title cscanv2-title">Scan Bill</div>
            <div className="scan-subtitle cscanv2-subtitle">Take photos of your bill(s)</div>
          </div>

          <div
            className="upload-section cscanv2-upload-card"
            onClick={handleFileUpload}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleFileUpload()}
          >
            <div className="cscanv2-icon-wrap cscanv2-upload-icon-wrap" aria-hidden="true">
              <LucideUpload className="cscanv2-lucide cscanv2-lucide-upload" strokeWidth={2.25} />
            </div>
            <div className="upload-title cscanv2-title">
              {isDragging ? 'Drop to upload' : 'Upload Bills'}
            </div>
            <div className="upload-subtitle cscanv2-subtitle">
              {isDragging
                ? 'photos, PDFs, .xlsx, .xls, .csv supported'
                : <>Choose from gallery or files <br />(multiple files supported)</>}
            </div>
          </div>
        </div>
      )}

      {/* Bill Memory: paste a bill as text (WhatsApp lists) — fewer AI tokens, the same lessons */}
      {bmTextOn && (
        <div className="bm-paste-text" style={{ margin: '8px 0' }}>
          <button type="button" className="suggestion-chip" onClick={() => setShowPaste(s => !s)}>Paste bill text</button>
          {showPaste && (
            <div style={{ marginTop: 8 }}>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="Paste the bill here (for example a WhatsApp list)"
                rows={6}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
              <button type="button" className="suggestion-chip" onClick={addPastedText} disabled={!pasteText.trim()}>Add bill text</button>
            </div>
          )}
        </div>
      )}

      {/* Hidden file inputs - UPDATED FOR MULTIPLE */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf,.xlsx,.xls,.xlsm,.csv,.ods,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        onChange={handleFileSelect}
        className="file-input"
        style={{ display: 'none' }}
        multiple
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="file-input"
        style={{ display: 'none' }}
        multiple
      />

      {/* Images/PDFs Preview - UPDATED FOR MULTIPLE */}
      {previewImages && previewImages.length > 0 && (
        <div className={`images-preview-container${isDragging ? ' cscanv2-dropping' : ''}`} style={{ position: 'relative' }}>
          {/* Without this the grid gives no sign it accepts a drop, and a user who has
              already queued one bill has no reason to believe dragging still works. */}
          {isDragging && (
            <div className="cscanv2-drop-overlay" aria-hidden="true">
              Drop to add more bills
            </div>
          )}
          <div className="preview-header">
            <h3>Selected Files ({selectedFiles.length})</h3>
            <div className="header-buttons">
              <button
                onClick={handleFileUpload}
                className="add-more-button header-add-button"
                disabled={isProcessing}
                type="button"
              >
                Add
              </button>
              <button
                onClick={resetScanner}
                className="reset-all-button"
                disabled={isProcessing}
                type="button"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Same Bill / Separate Bills toggle — only show when 2+ files */}
          {selectedFiles.length > 1 && !isProcessing && (
            <div className="bill-mode-toggle">
              <button
                type="button"
                className={`bill-mode-btn ${sameBillMode ? 'active' : ''}`}
                onClick={() => setSameBillMode(true)}
              >
                Pages of Same Bill
              </button>
              <button
                type="button"
                className={`bill-mode-btn ${!sameBillMode ? 'active' : ''}`}
                onClick={() => setSameBillMode(false)}
              >
                Separate Bills
              </button>
            </div>
          )}

          <div className="images-grid">
            {/* Iterating the entries, not the derived preview strings: the file, its
                thumbnail and its remove button all come from one row, so a tile can never
                show one bill's name over another's image. */}
            {fileEntries.map(({ id, file, preview }, index) => (
              <div key={id} className={`image-preview-item ${fileStatuses[id] === 'failed' ? 'file-failed' : ''} ${fileStatuses[id] === 'done' ? 'file-done' : ''}`}>
                {preview === 'PDF_PLACEHOLDER' ? (
                  <div className="pdf-preview-small">
                    <FontAwesomeIcon icon={faUpload} className="pdf-icon-small" />
                    <p className="pdf-name">{file?.name}</p>
                  </div>
                ) : (
                  <img
                    src={preview}
                    alt={`Receipt ${index + 1}`}
                    className="preview-image-small"
                    onClick={() => setSelectedImageModal({ src: preview, index })}
                    style={{ cursor: 'pointer' }}
                    onError={() => setError('Failed to load preview image')}
                  />
                )}
                <div className="image-number">#{index + 1}</div>
                {/* Per-file status badge */}
                {fileStatuses[id] && fileStatuses[id] !== 'pending' && (
                  <div className={`file-status-badge status-${fileStatuses[id]}`}>
                    {fileStatuses[id] === 'compressing' && 'CMP'}
                    {fileStatuses[id] === 'uploading' && 'UPL'}
                    {fileStatuses[id] === 'processing' && 'PRC'}
                    {fileStatuses[id] === 'done' && 'OK'}
                    {fileStatuses[id] === 'failed' && 'ERR'}
                  </div>
                )}
                <button
                  onClick={() => removeFile(id)}
                  className="remove-image-button"
                  disabled={isProcessing}
                  type="button"
                  title="Remove this image"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="preview-actions">
            <button
              onClick={() => processImage()}
              className="process-button"
              disabled={isProcessing || selectedFiles.length === 0}
              type="button"
            >
              {isProcessing
                ? `Processing ${currentProcessingIndex}/${selectedFiles.length}...`
                : `Confirm ${selectedFiles.length} Image${selectedFiles.length > 1 ? 's' : ''}`
              }
            </button>
          </div>
        </div>
      )}

      {/* Error message with retry option */}
      {error && (
        <div style={{
          textAlign: 'center',
          padding: '16px',
          margin: '16px 0',
          backgroundColor: '#fee',
          border: '1px solid #dc3545',
          borderRadius: '8px',
          color: '#dc3545',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {error}
          {failedFileIds.length > 0 && !isProcessing && (
            <button
              onClick={retryFailedFiles}
              type="button"
              style={{
                display: 'block',
                margin: '12px auto 0',
                padding: '8px 20px',
                background: '#000',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Retry {failedFileIds.length} Failed File{failedFileIds.length > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="processing-spinner"></div>
            <h3>Processing Your Receipt{selectedFiles.length > 1 ? 's' : ''}</h3>

            <p className="processing-file-info">
              {currentProcessingIndex > 0
                ? `${currentProcessingIndex} of ${selectedFiles.length} files processed`
                : selectedFiles.length > 1 ? `Processing ${selectedFiles.length} files in parallel...` : 'Processing...'
              }
            </p>

            <div className="processing-progress">
              <div
                className="progress-bar"
                style={{ width: `${processingProgress}%` }}
              ></div>
            </div>

            <p>{processingProgress.toFixed(0)}% Complete</p>

            <p style={{ marginTop: '-9px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
              Review  required
            </p>

            <button
              onClick={cancelProcessing}
              className="cancel-processing-btn"
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* OCR Real-time Overlay */}
      <OCRRealtimeOverlay
        show={showOCROverlay}
        vendorData={ocrVendorData}
        items={ocrItems}
        stage={ocrStage}
        progress={ocrProgress}
        stageName={ocrStageName}
        onCancel={handleCancelOCR}
      />

      {/* Tax detected — confirm before continuing */}
      {showTaxConfirm && (
        <div
          className="tax-confirm-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 100000,
            background: 'rgba(0,0,0,0.55)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: '20px'
          }}
          onClick={handleTaxConfirmCancel}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '14px', maxWidth: '380px', width: '100%',
              padding: '24px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '34px', marginBottom: '8px' }}>🧾</div>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.15rem', color: '#111827' }}>
              {confirmMessages.length === 1 && confirmMessages[0].startsWith('Tax') ? 'Tax detected' : 'Before you continue'}
            </h3>
            <div style={{ margin: '0 0 20px', fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.5, textAlign: 'left' }}>
              {confirmMessages.map((m, i) => (
                <p key={i} style={{ margin: '0 0 8px' }}>{confirmMessages.length > 1 ? '• ' : ''}{m}</p>
              ))}
              <p style={{ margin: 0, fontWeight: 600, textAlign: 'center' }}>Do you want to continue?</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={handleTaxConfirmCancel}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                  border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                  fontSize: '14px', fontWeight: 600
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTaxConfirmOk}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                  border: '1px solid #059669', background: '#059669', color: '#fff',
                  fontSize: '14px', fontWeight: 600
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extracted Items Preview (hidden while the tax-confirm popup is open) */}
      {(rawOcrText || extractedItems.length > 0) && !isProcessing && !showTaxConfirm && (
        <div className="extraction-results-wrapper">
          {/* Multi-page merge indicator */}
          {selectedFiles.length > 1 && (
            <div className="multi-page-merge-indicator">
              <span className="merge-icon">{sameBillMode ? 'SAME' : 'MULTI'}</span>
              <span className="merge-text">
                {sameBillMode
                  ? `Same bill (${selectedFiles.length} pages) → ${extractedItems.length} items`
                  : `${selectedFiles.length} separate bills → ${extractedItems.length} total items`
                }
                {failedFileIds.length > 0 && ` (${failedFileIds.length} failed)`}
              </span>
            </div>
          )}

          {/* Retry failed files button in results area */}
          {failedFileIds.length > 0 && !isProcessing && (
            <button
              onClick={retryFailedFiles}
              type="button"
              className="retry-failed-btn"
            >
              Retry {failedFileIds.length} Failed File{failedFileIds.length > 1 ? 's' : ''}
            </button>
          )}

          {/* Raw OCR Text Panel */}
          {rawOcrText && (
            <div className="raw-ocr-text-panel">
              <div className="raw-ocr-header">
                <h3 style={{ margin: 0 }}>Raw OCR Text</h3>
                <div className="raw-ocr-actions">
                  <button
                    type="button"
                    className="toggle-raw-text-btn"
                    onClick={() => setShowRawText(prev => !prev)}
                  >
                    {showRawText ? 'Hide' : 'Show'}
                  </button>
                  <button
                    type="button"
                    className="copy-raw-text-btn"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(rawOcrText);
                        setSuccess('Raw OCR text copied to clipboard');
                        setTimeout(() => setSuccess(null), 1500);
                      } catch (e) {
                        console.warn('Clipboard copy failed', e);
                      }
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              {showRawText && (
                <pre className="raw-ocr-text" style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto', marginTop: '8px', background: '#1f1f1f', color: '#eee', padding: '8px', borderRadius: '6px', fontSize: '0.8rem' }}>
                  {rawOcrText}
                </pre>
              )}
            </div>
          )}
          {/* Extracted Items Panel */}
          {extractedItems.length > 0 && (
            <div className="extracted-items-preview">
              <h3>Extracted Items ({extractedItems.length})</h3>
              {extractedItems.map((item, index) => (
                <div key={`${item.item_name}-${index}`} className="extracted-item">
                  <div className="item-info">
                    <div className="item-name">
                      {item.item_name || 'Unknown Item'}
                      {selectedFiles.length > 1 && item.sourceFileIndex && (
                        <span className="page-badge" title={`From ${item.sourceFile}`}>
                          P{item.sourceFileIndex}
                        </span>
                      )}
                    </div>
                    <div className="item-details">
                      {item.quantity || '1'} {item.unit || 'pc'} • {item.category || 'Miscellaneous'}
                    </div>
                  </div>
                  <div className="item-price">
                    ₹{item.unit_price || '0.00'}
                  </div>
                </div>
              ))}
              <button
                onClick={handleContinueWithItems}
                className="continue-button"
                type="button"
              >
                Continue with These Items
              </button>
            </div>
          )}
        </div>
      )}

      {/* Full-screen Image Modal */}
      {selectedImageModal && (
        <div className="image-modal-overlay" onClick={() => setSelectedImageModal(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={() => setSelectedImageModal(null)}
              aria-label="Close"
            >
              ✕
            </button>
            <img
              src={selectedImageModal.src}
              alt={`Receipt ${selectedImageModal.index + 1}`}
              className="image-modal-img"
            />
            <div className="image-modal-info">
              Image #{selectedImageModal.index + 1} of {previewImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

BillScanner.displayName = 'BillScanner';

export default BillScanner;
